import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useLocale } from "@/i18n";

export default function PaymentResult({ success }) {
  const { t } = useLocale();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(success ? "checking" : "canceled");

  useEffect(() => {
    if (!success || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.status === "completed" || res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        // tenta de novo
      }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [success, sessionId]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex items-center justify-center px-6" data-testid="payment-result-page">
      <div className="w-full max-w-md text-center">
        {state === "checking" && (
          <>
            <div className="h-10 w-10 mx-auto rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
            <h1 className="text-2xl font-bold mt-6">{t("pay_confirming")}</h1>
            <p className="text-sm text-zinc-500 mt-2">{t("pay_wait")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={64} weight="fill" className="text-amber-500 mx-auto" />
            <h1 className="text-2xl font-bold mt-6" data-testid="payment-success-title">{t("pay_success")}</h1>
            <p className="text-sm text-zinc-400 mt-2">{t("pay_success_sub")}</p>
            <Link
              to="/painel"
              data-testid="payment-back-panel-btn"
              className="mt-8 inline-flex w-full items-center justify-center h-12 rounded-xl bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors"
            >
              {t("pay_go_panel")}
            </Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <h1 className="text-2xl font-bold">{t("pay_almost")}</h1>
            <p className="text-sm text-zinc-400 mt-2">{t("pay_almost_sub")}</p>
            <Link to="/painel" className="mt-8 inline-flex w-full items-center justify-center h-12 rounded-xl bg-amber-500 text-zinc-950 font-bold">{t("pay_go_panel")}</Link>
          </>
        )}
        {state === "canceled" && (
          <>
            <XCircle size={64} weight="duotone" className="text-zinc-600 mx-auto" />
            <h1 className="text-2xl font-bold mt-6" data-testid="payment-cancel-title">{t("pay_canceled")}</h1>
            <p className="text-sm text-zinc-400 mt-2">{t("pay_canceled_sub")}</p>
            <Link
              to="/painel"
              data-testid="payment-cancel-back-btn"
              className="mt-8 inline-flex w-full items-center justify-center h-12 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-200 font-bold hover:border-amber-500/60 transition-colors"
            >
              {t("pay_back")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
